/**
 * author: Ananthapadmanabhan V K
 */

import "bootstrap/dist/css/bootstrap.min.css";
import { React, createContext, useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import "./App.scss";
import { AIReportJobStatus, EventType, POLLING_INTERVAL, PollerAction, PollerType, LS_AUTHORITY_KEY } from "./core/constants/common";
import ReportAIService from "./core/service/ReportAIService";
import ReportService from "./core/service/ReportService";
import "./i18n";
import RoutesList from "./router";

export const RegulationContext = createContext();
export const CompanyContext = createContext();
export const ButtonStatusContext = createContext();
export const LoaderContext = createContext();
export const SidebarContext = createContext();
export const PollerContext = createContext();
export const UserContext = createContext();
export const EventContext = createContext();
export const ChatShowContext = createContext();
export const PreferenceContext = createContext();
export const CompanyInfoContext = createContext();

let poller;

function App() {
  let [color, setColor] = useState("#383cc1");
  const [glbRegulation, setGlbRegulation] = useState("");
  const [glbRegulations, setGlbRegulations] = useState([]);

  const [glbCompany, setGlbCompany] = useState("");
  const [glbCompanies, setGlbCompanies] = useState([]);

  const [glbButtonDisable, setGlbButtonDisable] = useState(false);
  const [loaderDisplay, setLoaderDisplay] = useState();
  const [sidebarDisplay, setSidebarDisplay] = useState(true);
  const [pollerData, setPollerData] = useState({
    action: PollerAction.IDLE,
    companyId: 0,
    type: PollerType.NONE,
    jobId: 0,
    status: '',
    progress: '',
    jobType: 'AUTO_DRAFT',
    reportType: "GRI"
  });
  const [userDetails, setUserDetails] = useState(localStorage.getItem(LS_AUTHORITY_KEY) ? JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY)) : '');
  const [event, setEvent] = useState({
    event: '',
    data: '',
    status: ''
  });
  const [showChat, setShowChat] = useState('Global');
  const [preference, setPreference] = useState({
    language: "",
    hasLanguagesChosen: false
  });
  const [companyInfo, setCompanyInfo] = useState('');

  // Initialize Google Analytics
  useEffect(() => {
    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-B4EEHKNTY9';
    document.head.appendChild(script1);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'G-B4EEHKNTY9');

    return () => {
      // Cleanup: remove script on unmount
      const scripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  useEffect(() => {
    if (pollerData.companyId > 0
      && pollerData.type === PollerType.REPORT_BUILDER
      && pollerData.jobId > 0
      && pollerData.action === PollerAction.START
      && pollerData.progress === '') {
      startReportBuilderPoller();
    } else if (pollerData.type === PollerType.REPORT_BUILDER
      && pollerData.action === PollerAction.STOP) {
      stopReportBuilderPoller();
    }
  }, [pollerData]);


  const startReportBuilderPoller = () => {
    console.log(`Starting report buidler poller`);
    poller = setTimeout(function request() {
      let req;
      if (pollerData.jobType === 'CO_DRAFT') {
        req = ReportAIService.checkCoDraftJobStatus(pollerData.jobId)
      } else {
        req = ReportAIService.checkJobStatus(pollerData.jobId);
      }
      req.then(res => {
        if (res.status !== 200) {
          stopReportBuilderPoller();
        } else if (res.data.status === AIReportJobStatus.COMPLETED) {
          if (pollerData.jobType === 'CO_DRAFT') {
            completeCoDraftJob(pollerData.jobId);
          } else {
            if (!!pollerData.reportType && pollerData.reportType == 'ESRS' || pollerData.reportType == 'VSME') {
              updateESRSReportContent(pollerData.jobId)
            } else {
              collectReportContent(pollerData.jobId);
            }
          }
        } else if (res.data.status === AIReportJobStatus.FAILED) {
          toast.error('Report generation failed', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 1000
          });
          updateJobFailure(pollerData.jobId);
        } else if (res.data.status === AIReportJobStatus.IN_PROGRESS
          || res.data.status === AIReportJobStatus.NOT_STARTED) {
          setPollerData({
            ...pollerData,
            progress: res.data.progress
          });
        }
      }).catch(err => {
        stopReportBuilderPoller();
      });
      poller = setTimeout(request, POLLING_INTERVAL);
    }, POLLING_INTERVAL);
  };

  const stopReportBuilderPoller = () => {
    console.log(`Stopped report buidler poller`);
    if (!poller) return;

    clearTimeout(poller);
  };

  const collectReportContent = (jobId) => {
    setPollerData({
      ...pollerData,
      action: PollerAction.STOP,
      jobId: jobId,
      progress: ''
    });
    ReportAIService.collectReportData(jobId).then(res => {
      if (res.status !== 200) return;

      updateReportContent(res.data);
    }).catch(err => { })
  };

  const updateReportContent = (payload) => {
    ReportService.updateReportContent(payload).then(res => {
      if (res.status !== 200) return;

      setPollerData({
        ...pollerData,
        jobId: 0,
        action: PollerAction.STOP,
        status: AIReportJobStatus.COMPLETED,
        progress: ''
      });
      setEvent({
        event: EventType.REFRESH_REPORT_LIST,
        data: res.reportId,
        status: AIReportJobStatus.COMPLETED
      });
    }).catch(err => { })
  };

  const updateESRSReportContent = (jobId) => {
    const payload = { jobId: jobId }
    ReportService.updateESRSReportContent(payload).then(res => {
      if (res.status !== 200) return;

      setPollerData({
        ...pollerData,
        jobId: 0,
        action: PollerAction.STOP,
        status: AIReportJobStatus.COMPLETED,
        progress: ''
      });
      setEvent({
        event: EventType.REFRESH_REPORT_LIST,
        data: res.reportId,
        status: AIReportJobStatus.COMPLETED
      });
    }).catch(err => { })
  };

  const updateJobFailure = (jobId) => {
    setPollerData({
      ...pollerData,
      jobId: 0,
      action: PollerAction.STOP,
      status: AIReportJobStatus.FAILED,
      progress: ''
    });
    ReportService.updateJobStatus(jobId, 3).then(res => {
      if (res.status !== 200) return;
      setEvent({
        event: EventType.REFRESH_REPORT_LIST,
        data: jobId,
        status: AIReportJobStatus.FAILED
      });
    }).catch(err => { });
  };

  const completeCoDraftJob = (jobId) => {
    setPollerData({
      ...pollerData,
      reportData: pollerData.reportData,
      jobId: 0,
      status: AIReportJobStatus.COMPLETED,
      progress: ''
    });
    ReportService.updateJobStatus(jobId, 2).then(res => {
      if (res.status == 200) {
        setPollerData({
          ...pollerData,
          reportData: pollerData.reportData,
          jobId: 0,
          action: PollerAction.STOP,
          status: AIReportJobStatus.COMPLETED,
          progress: ''
        });
      } else {
        setPollerData({
          ...pollerData,
          jobId: 0,
          action: PollerAction.STOP,
          status: AIReportJobStatus.FAILED,
          progress: ''
        });
      }
    }).catch(err => { });
  };

  return (
    <BrowserRouter>
      <div className="App" >
        <div className="wrapper">
          <ButtonStatusContext.Provider value={{ glbButtonDisable, setGlbButtonDisable }} >
            <EventContext.Provider value={{ event, setEvent }}>
              <ChatShowContext.Provider value={{ showChat, setShowChat }}>
                <UserContext.Provider value={{ userDetails, setUserDetails }}>
                  <CompanyContext.Provider
                    value={{
                      glbCompany,
                      setGlbCompany,
                      glbCompanies,
                      setGlbCompanies
                    }}>
                    <RegulationContext.Provider
                      value={{
                        glbRegulation,
                        setGlbRegulation,
                        glbRegulations,
                        setGlbRegulations,
                      }}>

                      <SidebarContext.Provider value={{ sidebarDisplay, setSidebarDisplay }}>
                        <PollerContext.Provider value={{ pollerData, setPollerData }}>
                          <LoaderContext.Provider value={{ loaderDisplay, setLoaderDisplay, }}>
                            <PreferenceContext.Provider value={{ preference, setPreference }}>
                              <CompanyInfoContext.Provider value={{ companyInfo, setCompanyInfo }}>
                                <RoutesList />
                                <div className={`${loaderDisplay ? "spinner-Body" : "d-none"}`}>
                                  <div className="spinner-8">            </div>
                                </div>
                              </CompanyInfoContext.Provider>
                            </PreferenceContext.Provider>
                          </LoaderContext.Provider>
                        </PollerContext.Provider>
                      </SidebarContext.Provider>
                    </RegulationContext.Provider>
                  </CompanyContext.Provider>
                </UserContext.Provider>
              </ChatShowContext.Provider>
            </EventContext.Provider>
          </ButtonStatusContext.Provider>
        </div>
        <ToastContainer autoClose={5000} />
      </div>
    </BrowserRouter>
  );
}

export default App;
